import type React from "react";
// Side-effect import: registers suggestion renderers in the registry
import "../../suggestion-renderers/pair-networking/register";
import { SuggestionRendererRegistry } from "../../suggestion-renderers/suggestion-renderer.registry";
import type { Suggestion } from "@shared/schemas/suggestion.schemas";

export interface GroupingSuggestionsListProps {
  readonly suggestions: readonly Suggestion[];

  readonly className?: string;
}

/**
 * GroupingSuggestionsList - Renders suggestions using registry-based dispatch
 *
 * Looks up the appropriate renderer for each suggestion based on
 * the suggestion type (e.g., PROFILE_BASED).
 *
 * Shows bullet points only when there are multiple suggestions.
 * Single suggestions are rendered without bullets for cleaner presentation.
 */
export function GroupingSuggestionsList({
  suggestions,
  className = "",
}: GroupingSuggestionsListProps): React.JSX.Element {
  const hasMultipleSuggestions = suggestions.length > 1;

  // Single suggestion: render without bullet points
  if (!hasMultipleSuggestions && suggestions.length === 1) {
    const suggestion = suggestions[0];
    if (suggestion !== undefined) {
      const renderer = SuggestionRendererRegistry.getRenderer(
        suggestion.suggestionType,
      );
      return <div className={className}>{renderer(suggestion)}</div>;
    }
  }

  return (
    <ul className={`list-disc ml-6 ${className}`}>
      {suggestions.map((suggestion, index) => {
        const renderer = SuggestionRendererRegistry.getRenderer(
          suggestion.suggestionType,
        );
        return (
          // and don't have stable unique IDs in this context
          <li
            key={`${suggestion.suggestionType}-${index}`}
            className="leading-normal"
          >
            {renderer(suggestion)}
          </li>
        );
      })}
    </ul>
  );
}
